import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  FaMoneyBillWave,
  FaUsers,
  FaNewspaper,
  FaVolumeUp,
} from "react-icons/fa";
import { MdAccessTimeFilled } from "react-icons/md";
import { IoMdTime } from "react-icons/io";
import "./dashboard.css";
import { LuLayoutDashboard } from "react-icons/lu";

const EmployeeDashboard = () => {
  // State variables
  const [totalSalary, setTotalSalary] = useState(0);
  const [totalEmployees, setTotalEmployees] = useState(35);
  const [totalHR, setTotalHR] = useState(1);
  const [pendingLeaves, setPendingLeaves] = useState(0); // Added state for pending leaves
  const [announcements, setAnnouncements] = useState([]);
  const [checkedInUsersCount, setCheckedInUsersCount] = useState(0); // New state for checked-in users
  const [error, setError] = useState(null);

  // Fetch announcements based on role
  useEffect(() => {
    const userRole = localStorage.getItem("role");
    if (!userRole) {
      console.error("Role is not found in session storage");
      return;
    }

    const fetchAnnouncements = async () => {
      try {
        const response = await axios.get(
          "https://backend.ems.hiradentallab.com/api/announcements/role/announcement",
          { params: { role: userRole } }
        );
        if (response.data && response.data.length > 0) {
          setAnnouncements(response.data);
        } else {
          setAnnouncements([]);
        }
      } catch (error) {
        console.error("Error fetching announcements:", error);
        setError("Failed to load announcements");
      }
    };

    fetchAnnouncements();
  }, []);

  // Fetch dashboard data, including checked-in users count
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch total salary
        const salaryResponse = await axios.get("https://backend.ems.hiradentallab.com/api/user/total-salaries");
        setTotalSalary(salaryResponse.data.totalSalary || 0);

        // Fetch employee and HR counts
        const countResponse = await axios.get("https://backend.ems.hiradentallab.com/api/user/count/roles");
        setTotalEmployees(countResponse.data.employeeCount || 35);
        setTotalHR(countResponse.data.hrCount || 1);

        // Fetch count of pending leave requests
        const leaveResponse = await axios.get("https://backend.ems.hiradentallab.com/api/leaves/count-pending");
        setPendingLeaves(leaveResponse.data.count || 0);

        // Fetch checked-in users count for today
        const checkInResponse = await axios.get("https://backend.ems.hiradentallab.com/api/attendance/count-checked-in-today");
        setCheckedInUsersCount(checkInResponse.data.data.checkedInCount || 0); // Fixed here
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <div className="container-fluid">
    <div className="dashboard">
      <div className="header">
        <h1 className="dashboard-title">
          <LuLayoutDashboard /> Dashboard
        </h1>
        <div className="header-links">
          <Link to="/" className="header-link">
            Not Checked-In <MdAccessTimeFilled />
          </Link>
          <button className="header-button">
            10:00 pm <IoMdTime />
          </button>
          <button className="header-button">
            00:00 <IoMdTime />
          </button>
        </div>
        <hr />
      </div>

      <div className="card-container">
        <section>
          <div className="row">
            <div className="col-lg-4 col-md-6 col-sm-12 p-1">
              <div className="card">
                <div className="card-header">
                  Salaries <FaMoneyBillWave />
                </div>
                <div className="card-body text-center">
                  <h5 className="card-title">{totalSalary}</h5>
                  <p className="card-text">Giving Salaries To Active Accounts</p>
                </div>
                <div className="card-footer">
                  <Link to="/emp-info" className="card-footer-btn">
                    See Salaries
                  </Link>
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 col-sm-12 p-1">
              <div className="card">
                <div className="card-header">
                  Currently Working <FaUsers />
                </div>
                <div className="card-body text-center d-flex justify-content-center align-items-center">
                  <div id="announcements">
                    <div className="dashboard-circles">
                      <div className="dashboard-circle-container">
                      <strong> {checkedInUsersCount}/{totalEmployees + totalHR}</strong> 
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-footer">
                  <Link to="/current-atd" className="card-footer-btn">
                    See Attendance
                  </Link>
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 col-sm-12 p-1">
              <div className="card">
                <div className="card-header">
                  Total Employees <FaUsers />
                </div>
                <div className="card-body text-center">
                  <h5 className="card-title">{totalEmployees}</h5>
                  <p className="card-text">All Employees Of Company</p>
                </div>
                <div className="card-footer">
                  <Link to="/emp-info" className="card-footer-btn">
                    See Employee
                  </Link>
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 col-sm-12 p-1">
              <div className="card">
                <div className="card-header">
                  Total HR <FaUsers />
                </div>
                <div className="card-body text-center">
                  <h5 className="card-title">{totalHR}</h5>
                  <p className="card-text">All HR Of Company</p>
                </div>
                <div className="card-footer">
                  <Link to="/hr-info" className="card-footer-btn">
                    See HR
                  </Link>
                </div>
              </div>
            </div>

            {/* Pending Leaves Card */}
            <div className="col-lg-4 col-md-6 col-sm-12 p-1">
              <div className="card">
                <div className="card-header">
                  Leave Requests <FaNewspaper />
                </div>
                <div className="card-body text-center">
                  <h5 className="card-title">{pendingLeaves}</h5>
                  <p className="card-text">Requests Notifications</p>
                </div>
                <div className="card-footer">
                  <Link to="/pending-leave" className="card-footer-btn">
                    See Leaves
                  </Link>
                </div>
              </div>
            </div>

            {/* Announcement Card */}
            <div className="col-lg-4 p-1">
              <div className="card" id="dashboard-card">
                <div className="card-header" id="dashboard-card-header">
                  Announcements <FaVolumeUp />
                </div>
                <div className="card-body" id="dashboard-card-body">
                  <div id="announcements">
                    {error ? (
                      <h5 className="card-title">{error}</h5>
                    ) : announcements.length > 0 ? (
                      announcements.map((announcement, index) => (
                        <div key={index}>
                          <h5 className="card-title">{announcement.title}</h5>
                          <p className="card-text">{announcement.content}</p>
                        </div>
                      ))
                    ) : (
                      <h5 className="card-title">No Announcements For Today</h5>
                    )}
                  </div>
                </div>
                <div className="card-footer">
                  <Link to="/emp-announcement" className="card-footer-btn">See Announcements</Link>
                </div>
              </div>
            </div>

          </div>
        </section>
      </div>
    </div>
    </div>
  );
};

export default EmployeeDashboard;
